import { createSelector, createFeatureSelector } from '@ngrx/store';
import {
  AHdeviceTypeEnum,
  AzzurroHubDataType,
  AHpsc100Type,
  ConnextData,
  DeviceType,
  EvChType,
  mapAHevChargerCANTypeToEvChType,
  NodeType,
  PowerFlowGraphMode,
  checkFwVersion
} from 'ngx-core-services';

import { SystemsState } from '../state/system.state';

const AZZURRO_HUB_MIN_FW_FOR_RT = '900.300';
const CONNEXT_MIN_FW_FOR_RT = '1.42.2099';

export type RealTimeTelemetryUnavailableReason =
  | 'disconnected'
  | 'firmware_update_required';

export interface RealTimeTelemetryAvailability {
  canUseRealTime: boolean;
  unavailableReason: RealTimeTelemetryUnavailableReason | null;
}

const RT_AVAILABILITY_HIDDEN: RealTimeTelemetryAvailability = {
  canUseRealTime: false,
  unavailableReason: null,
};

const RT_AVAILABILITY_ENABLED: RealTimeTelemetryAvailability = {
  canUseRealTime: true,
  unavailableReason: null,
};

const RT_AVAILABILITY_DISCONNECTED: RealTimeTelemetryAvailability = {
  canUseRealTime: false,
  unavailableReason: 'disconnected',
};

const RT_AVAILABILITY_FW_UPDATE_REQUIRED: RealTimeTelemetryAvailability = {
  canUseRealTime: false,
  unavailableReason: 'firmware_update_required',
};


export const selectSystemState = createFeatureSelector<SystemsState>('systemState');

export const selectSystemList = createSelector(
  selectSystemState,
  (state: SystemsState) => state.systemList
);

export const selectSystemById = (systemId: string) => createSelector(
  selectSystemList,
  (systemList) => systemList[systemId]
);

export const selectedSystem = createSelector(
  selectSystemState,
  (state: SystemsState) => {
    const selectedSystem = state.selectedSystemUid;
    if (!selectedSystem) {
      return null;
    }
    return state.systemList[selectedSystem];
  }
);

export const selectedSystemUid = createSelector(
  selectSystemState,
  (state: SystemsState) => state.selectedSystemUid,
);

export const selectedSystemMetadata = createSelector(
  selectSystemState,
  (state: SystemsState) => {
    const selectedSystem = state.selectedSystemUid;
    if (!selectedSystem) {
      return null;
    }
    return state.systemList[selectedSystem]?.metadata;
  }
);

export const selectTemporarySystemState = createSelector(
  selectSystemState,
  (state: SystemsState) => state.temporarySystemState
);

export const selectTemporarySystemStateAndSelectedSystemUid = createSelector(
  selectTemporarySystemState,
  selectedSystemUid,
  (temporarySystemState, selectedSystemUid) => {
    return { temporarySystemState, selectedSystemUid };
  }
);

export const selectedSystemStructure = createSelector(
  selectSystemState,
  (state: SystemsState) => {
    const selectedSystem = state.selectedSystemUid;
    if (!selectedSystem) {
      return null;
    }
    return state.systemList[selectedSystem]?.metadata?.structure;
  }
);

export const selectAwsConfigBySystemId = (systemId: string) => createSelector(
  selectSystemById(systemId),
  (system) => system?.aws?.config
);

export const selectedSystemMetadataNodesType = createSelector(
  selectSystemState,
  (state: SystemsState) =>  {
    const selectedSystem = state.selectedSystemUid;
    if (!selectedSystem) {
      return null;
    }
    switch (state.systemList[selectedSystem]?.metadata?.structure) {
      case PowerFlowGraphMode.OnlyGeneratedPower:
        return ['solar'] as NodeType[];
      case PowerFlowGraphMode.WithBattery:
        return ['solar', 'battery', 'home', 'grid'] as NodeType[];
      case PowerFlowGraphMode.WithImportExport:
        return ['solar', 'home', 'grid'] as NodeType[];
      default:
        return ['solar'] as NodeType[];
    }
  }
);

export const selectedSystemTz = createSelector(
  selectSystemState,
  (state: SystemsState) => {
    const selectedSystem = state.selectedSystemUid;
    if (!selectedSystem) {
      return null;
    }
    return state.systemList[selectedSystem]?.metadata?.tz;
  }
);

export const selectedSystemDevices = (devicesTypes?: DeviceType[]) => createSelector(
  selectSystemState,
  (state: SystemsState) => {
    const selectedSystem = state.selectedSystemUid;
    if (!selectedSystem) {
      return null;
    }

    if (!devicesTypes) {
      return state.systemList[selectedSystem]?.devices;
    }

    return state.systemList[selectedSystem]?.devices.filter(device => devicesTypes.includes(device.type));
  }
);

export const selectedSystemVerifiedUninstallations = createSelector(
  selectSystemState,
  (state: SystemsState) => {
    const selectedSystem = state.selectedSystemUid;
    if (!selectedSystem) {
      return null;
    }
    return state.systemList[selectedSystem]?.metadata?.verifiedUninstallations;
  }
);

export const selectedSystemVerifiedDevices = createSelector(
  selectSystemState,
  (state: SystemsState) => {
    const selectedSystem = state.selectedSystemUid;
    if (!selectedSystem) {
      return null;
    }
    return state.systemList[selectedSystem]?.metadata?.verifiedDevices;
  }
);

export const selectedSystemHistoricalDeviceList = createSelector(
  selectSystemState,
  (state: SystemsState) => {
    const selectedSystem = state.selectedSystemUid;
    if (!selectedSystem) {
      return null;
    }
    const uninstallations = state.systemList[selectedSystem]?.metadata?.verifiedUninstallations ?? [];
    const enabledDevices = state.systemList[selectedSystem]?.metadata?.verifiedDevices ?? [];

    return uninstallations?.concat(enabledDevices);
  }
);

export const selectedSystemMonitorPref = createSelector(
  selectSystemState,
  (state: SystemsState) => {
    const selectedSystem = state.selectedSystemUid;
    if (!selectedSystem) {
      return null;
    }
    return state.systemList[selectedSystem]?.monitoringType;
  }
);

export const selectSystemMetadata = (systemId: string) => createSelector(
  selectSystemById(systemId),
  (system) => {
    if (!system?.metadata) {
      return null;
    }

    return system.metadata;
  }
);

export const selectSystemMonitoringType = (systemId: string) => createSelector(
  selectSystemById(systemId),
  (system) => system?.monitoringType
);

export const selectedSystemConnextInfoSn = createSelector(
  selectSystemState,
  (state: SystemsState) => {
    const selectedSystem = state.selectedSystemUid;
    if (!selectedSystem || state.systemList[selectedSystem]?.monitoringType !== 'Connext') {
      return null;
    }

    const data = state.systemList[selectedSystem]?.data as Partial<ConnextData> | undefined;
    return data?.info?.sn?.sn ?? null;
  }
);

export const selectedSystemConnextData = createSelector(
  selectSystemState,
  (state: SystemsState) => {
    const selectedSystem = state.selectedSystemUid;
    if (!selectedSystem) {
      return null;
    }

    if (state.systemList[selectedSystem]?.monitoringType !== 'Connext') {
      return null;
    }

    return state.systemList[selectedSystem]?.data as Partial<ConnextData>;
  }
);

export const selectedSystemAzzurroHubData = createSelector(
  selectSystemState,
  (state: SystemsState) => {
    const selectedSystem = state.selectedSystemUid;
    if (!selectedSystem) {
      return null;
    }
    if (state.systemList[selectedSystem]?.monitoringType !== 'AzzurroHub') {
      return null;
    }

    return state.systemList[selectedSystem]?.data as Partial<AzzurroHubDataType>;
  }
);

export const selectAzzurroHubSystemScanState = createSelector(
  selectSystemState,
  (state: SystemsState) => {
    const selectedSystem = state.selectedSystemUid;
    if (!selectedSystem) {
      return null;
    }
    if (state.systemList[selectedSystem]?.monitoringType !== 'AzzurroHub') {
      return null;
    }

    const azzurroHubData = state.systemList[selectedSystem]?.data as Partial<AzzurroHubDataType>;

    if (!azzurroHubData.config) {
      return null;
    }

    return azzurroHubData.config?.scanDevices;
  }
);


export const selectRealTimeTelemetryAvailability = createSelector(
  selectSystemState,
  (state: SystemsState): RealTimeTelemetryAvailability => {
    const selectedSystem = state.selectedSystemUid;
    if (!selectedSystem) {
      return RT_AVAILABILITY_HIDDEN;
    }

    const system = state.systemList[selectedSystem];
    if (!system || system.monitoringType === 'Solarman') {
      return RT_AVAILABILITY_HIDDEN;
    }

    const isCertifiedPlant = system.source === 'aws';
    if (!isCertifiedPlant) {
      return RT_AVAILABILITY_HIDDEN;
    }

    if (system.monitoringType === 'AzzurroHub') {
      const azzurroHubData = system.data as Partial<AzzurroHubDataType>;
      if (!azzurroHubData) {
        return RT_AVAILABILITY_HIDDEN;
      }

      const isAzzurroHubOnline = azzurroHubData.info?.connection?.status === 'connected';
      if (!isAzzurroHubOnline) {
        return RT_AVAILABILITY_DISCONNECTED;
      }

      if (checkFwVersion(azzurroHubData.info?.fw?.currVer, AZZURRO_HUB_MIN_FW_FOR_RT)) {
        return RT_AVAILABILITY_ENABLED;
      }

      return RT_AVAILABILITY_FW_UPDATE_REQUIRED;
    }

    if (system.monitoringType === 'Connext') {
      const connextData = system.data as Partial<ConnextData>;
      if (!connextData) {
        return RT_AVAILABILITY_HIDDEN;
      }

      const isConnextOnline = connextData?.info?.connection?.status === 'connected';
      if (!isConnextOnline) {
        return RT_AVAILABILITY_DISCONNECTED;
      }

      if (CONNEXT_MIN_FW_FOR_RT) {
        if (checkFwVersion(connextData.info?.fw?.fw_ver, CONNEXT_MIN_FW_FOR_RT)) {
          return RT_AVAILABILITY_ENABLED;
        }
        return RT_AVAILABILITY_FW_UPDATE_REQUIRED;
      }

      return RT_AVAILABILITY_ENABLED;
    }

    return RT_AVAILABILITY_HIDDEN;
  }
);

export const selectCanUseRealTime = createSelector(
  selectRealTimeTelemetryAvailability,
  (availability) => availability.canUseRealTime,
);

export const selectSelectedEvcharge = (evchargeId: string) => createSelector(
  selectSystemState,
  (state: SystemsState) => {
    const selectedSystem = state.selectedSystemUid;
    if (!selectedSystem) {
      return null;
    }

    const system = state.systemList[selectedSystem];
    if ( system.monitoringType === 'AzzurroHub') {
      const azzurroHubData = system.data as Partial<AzzurroHubDataType>;
      if (!azzurroHubData.evCharger485 || !azzurroHubData.evChargerCAN) {
        return null;
      }

      const evch = azzurroHubData.evChargerCAN?.find(evcharge => evcharge.sn === evchargeId) ?? azzurroHubData.evCharger485?.find(evcharge => evcharge.sn === evchargeId) ?? null;

      if (!evch) {
        return null;
      }

      return mapAHevChargerCANTypeToEvChType(evch)
    }

    if (system.monitoringType === 'Connext') {
      const connextData = system.data as Partial<ConnextData>;
      return (connextData.devices?.find(evcharge => evcharge.sn === evchargeId) as EvChType) ?? null;
    }

    return null;
  }
);

export const selectSelectedHeatpump = (heatpumpId: string) => createSelector(
  selectSystemState,
  (state: SystemsState) => {
    const selectedSystem = state.selectedSystemUid;
    if (!selectedSystem) {
      return null;
    }

    const system = state.systemList[selectedSystem];
    if ( system.monitoringType === 'AzzurroHub') {
      const azzurroHubData = system.data as Partial<AzzurroHubDataType>;
      if (!azzurroHubData.heatPump) {
        return null;
      }

      const heatpump = azzurroHubData.heatPump.find(hp => hp.sn === heatpumpId) ?? null;

      if (!heatpump) {
        return null;
      }

      return heatpump;
    }
    return null;
  }
);

export const selectSelectedPsc100 = (psc100Id: string) => createSelector(
  selectSystemState,
  (state: SystemsState) => {
    const selectedSystem = state.selectedSystemUid;
    if (!selectedSystem) {
      return null;
    }

    const system = state.systemList[selectedSystem];
    if (!system || system.monitoringType !== 'AzzurroHub') {
      return null;
    }

    const azzurroHubData = system.data as Partial<AzzurroHubDataType> | undefined;
    if (!azzurroHubData?.PSC100) {
      return null;
    }

    return (azzurroHubData.PSC100.find((psc100) => psc100.sn === psc100Id) as AHpsc100Type | undefined) ?? null;
  }
);

export const selectedSystemSource = createSelector(
  selectSystemState,
  (state: SystemsState) => {
    const selectedSystem = state.selectedSystemUid;
    if (!selectedSystem) {
      return null;
    }

    return state.systemList[selectedSystem]?.source;
  }
);

/**
 * Base selector: returns the solarSensor array for the selected system
 * Returns null if not AzzurroHub or no system selected
 */
export const selectAzzurroHubSolarSensors = createSelector(
  selectSystemState,
  (state: SystemsState) => {
    const selectedSystem = state.selectedSystemUid;
    if (!selectedSystem) {
      return null;
    }
    if (state.systemList[selectedSystem]?.monitoringType !== 'AzzurroHub') {
      return null;
    }

    const azzurroHubData = (state.systemList[selectedSystem]?.data as Partial<AzzurroHubDataType>) ?? {};
    const sensors = azzurroHubData.solarSensor ?? [];
    return sensors;
  }
);

/**
 * FlowChart (instant values): true only if a solarSensor exists with removed: false
 */
export const selectAzzurroHubHasActiveSolarSensor = createSelector(
  selectAzzurroHubSolarSensors,
  (solarSensors) => solarSensors?.some(sensor => !sensor.removed) ?? false
);

/**
 * Daily Chart (historical): true if any solarSensor exists (even with removed: true)
 */
export const selectAzzurroHubHasAnySolarSensor = createSelector(
  selectAzzurroHubSolarSensors,
  (solarSensors) => (solarSensors?.length ?? 0) > 0
);

export const selectedSystemVirtualRTStatus = createSelector(
  selectSystemState,
  (state: SystemsState) => {
    const selectedSystem = state.selectedSystemUid;
    if (!selectedSystem) {
      return null;
    }
    return state.systemList[selectedSystem]?.virtualRTStatus ?? null;
  }
);

export const selectSystemVirtualRTStatusBySystemId = (systemId: string) => createSelector(
  selectSystemState,
  (state: SystemsState) => state.systemList[systemId]?.virtualRTStatus ?? null
);

export const selectObservedSystemsWithMultipleObservers = createSelector(
  selectSystemState,
  (state: SystemsState) => {
    return Object.keys(state.observers)
      .filter(systemId => {
        const observerCount = Object.keys(state.observers[systemId] ?? {}).length;
        return observerCount > 1;
      })
      .map(systemId => state.systemList[systemId]);
  }
);

export interface InverterWithLoggerData {
  id: string;
  loggerId: string | null;
  deviceSn: string | null;
  arm: string | null;
  dspm: string | null;
  dsps: string | null;
  loggerFirmwareVer: string | null;
  connectionStatus: 'connected' | 'disconnected' | null;
  fotaStatus: string | null;
  modbusAdr?: number;
  // AzzurroHub-specific fields
  ahUpdating?: boolean;
  ahCountry?: number;
  ahRegion?: number;
  ahSafetyVer?: string;
  ahGatewayOnline?: boolean;
  ahOperational?: string;
  ahModuleProgress?: {
    arm?: { status: number; progress: number } | null;
    dspm?: { status: number; progress: number } | null;
    dsps?: { status: number; progress: number } | null;
    safety?: { status: number; progress: number } | null;
    sofar?: { status: number; progress: number } | null;
  };
  /** Whether this device is part of the certified plant composition (system.aws.devices). */
  isCertified?: boolean;
}

export const selectedSystemInvertersWithLoggerData = createSelector(
  selectSystemState,
  (state: SystemsState): InverterWithLoggerData[] | null => {
    const selectedSystem = state.selectedSystemUid;
    if (!selectedSystem) {
      return null;
    }

    const system = state.systemList[selectedSystem];
    if (!system || !system.metadata) {
      return null;
    }

    // ─── AzzurroHub: inverters come from system.data.inverter[] ───
    if (system.monitoringType === 'AzzurroHub') {
      const azzurroHubData = system.data as Partial<AzzurroHubDataType> | undefined;
      const ahInverters = azzurroHubData?.inverter;
      if (!ahInverters || ahInverters.length === 0) {
        return null;
      }

      const gatewaySN = azzurroHubData?.loggerId ?? null;
      const isGatewayOnline = azzurroHubData?.info?.connection?.status === 'connected';
      const operational = azzurroHubData?.config?.operational;

      // Cross-reference with certified plant devices to flag uncertified inverters
      const certifiedSNs = new Set(
        (system.metadata?.verifiedDevices ?? []).map(d => d.sn.toUpperCase())
      );

      return ahInverters.map((inv): InverterWithLoggerData => ({
        id: inv.sn?.toUpperCase() ?? '',
        loggerId: gatewaySN,
        deviceSn: inv.sn ?? null,
        arm: inv.arm ?? null,
        dspm: inv.dspm ?? null,
        dsps: inv.dsps ?? null,
        loggerFirmwareVer: null,
        // Inverter is reachable only if BOTH gateway AND inverter are connected
        connectionStatus: (isGatewayOnline && inv.connected) ? 'connected' : 'disconnected',
        fotaStatus: null,
        modbusAdr: inv.modbusAdr,
        ahUpdating: inv.updating ?? false,
        ahCountry: inv.country,
        ahRegion: inv.region,
        ahSafetyVer: inv.safetyVer ?? undefined,
        ahGatewayOnline: isGatewayOnline ?? false,
        ahOperational: operational ?? 'normal',
        ahModuleProgress: {
          arm: inv.upd_arm ?? null,
          dspm: inv.upd_dspm ?? null,
          dsps: inv.upd_dsps ?? null,
          safety: inv.upd_safety ?? null,
          sofar: inv.upd_sofar ?? null,
        },
        isCertified: certifiedSNs.has(inv.sn?.toUpperCase() ?? ''),
      }));
    }

    // ─── Standard path: stick loggers ───
    const inverters = system.devices?.filter(device => device.type === 'inverter') ?? [];
    if (inverters.length === 0) {
      return null;
    }

    const loggers = system.aws?.loggers ?? [];

    // Flatten all (logger, device) pairs so each inverter can independently
    // match its own entry, even when two inverters share the same stick/logger
    const flatEntries = loggers.flatMap(l =>
      (l.devices ?? []).map(d => ({ logger: l, device: d }))
    );

    return inverters.map((inverter): InverterWithLoggerData => {
      const inverterId = inverter.id.toUpperCase();

      // Find all matching entries for this inverter and pick the most recent by lastUpdate
      const matches = flatEntries.filter(e => e.device.sn?.toUpperCase() === inverterId);

      if (matches.length === 0) {
        return {
          id: inverterId,
          loggerId: null,
          deviceSn: null,
          arm: null,
          dspm: null,
          dsps: null,
          loggerFirmwareVer: null,
          connectionStatus: null,
          fotaStatus: null,
        };
      }

      const lastMatch = matches.reduce((a, b) => {
        const dateA = a.device.lastUpdate ? new Date(a.device.lastUpdate).getTime() : 0;
        const dateB = b.device.lastUpdate ? new Date(b.device.lastUpdate).getTime() : 0;
        return dateB > dateA ? b : a;
      });

      return {
        id: inverterId,
        loggerId: lastMatch.logger.loggerId ?? null,
        deviceSn: lastMatch.logger.loggerId ?? null,
        arm: lastMatch.device.arm ?? null,
        dspm: lastMatch.device.dspm ?? null,
        dsps: lastMatch.device.dsps ?? null,
        loggerFirmwareVer: lastMatch.logger.info?.fw?.firmwareVer ?? null,
        connectionStatus: (lastMatch.logger.info?.connection?.status as 'connected' | 'disconnected') ?? null,
        fotaStatus: (lastMatch.device as any)?.fota_status ?? null,
      };
    });
  }
);

/**
 * Push-state for the LEGACY Solarman upgrade completion (stick logger + single
 * modules): those flows have no completion channel of their own, so completion
 * is observed on the fields kept fresh by the onPlantStatusUpdate subscription.
 * Reads `inverter[]` (NOT the deprecated `devices[]`) and the logger `info.fw`.
 * Returns null until the logger reaches the store (seed gap: the resolvers do
 * not project loggers — pushes graft them on the first change).
 */
export interface SolarmanUpgradePushState {
  /** Logger own firmware (info.fw.firmwareVer) — stick completion signal. */
  loggerFwVer: string | null;
  /** Per-inverter FOTA flag ('1' updating, '0' idle) — module completion signal. */
  fotaStatus: string | null;
  updating: boolean;
  moduleVersions: { ARM?: string; DSPM?: string; DSPS?: string } | null;
}

export const selectSolarmanUpgradePushState = (loggerSn: string, inverterSn: string) =>
  createSelector(selectSystemState, (state: SystemsState): SolarmanUpgradePushState | null => {
    const selected = state.selectedSystemUid;
    if (!selected) {
      return null;
    }
    const loggers = state.systemList[selected]?.aws?.loggers ?? [];
    const targetLogger = loggerSn.toUpperCase();
    const logger = loggers.find((l) => `${l.loggerId ?? ''}`.toUpperCase() === targetLogger);
    if (!logger) {
      return null;
    }
    const targetInverter = inverterSn.toUpperCase();
    const inverter = logger.inverter?.find((d) => d.sn?.toUpperCase() === targetInverter);
    return {
      loggerFwVer: logger.info?.fw?.firmwareVer ?? null,
      fotaStatus: inverter?.fota_status ?? null,
      updating: !!inverter?.updating,
      moduleVersions: inverter ? { ARM: inverter.arm, DSPM: inverter.dspm, DSPS: inverter.dsps } : null,
    };
  });
